import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, LogOut, MessageCircle } from 'lucide-react';
import { BID_SUPPORT_WHATSAPP_URL } from '@lms/shared';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary caught error]:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleClearAndLogin = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-surface flex items-center justify-center p-6 text-on-surface">
          <div className="max-w-md w-full bg-surface-container-lowest p-8 rounded-2xl shadow-level3 border border-outline-variant/10 text-center space-y-6 animate-fadeIn">
            <div className="w-16 h-16 bg-error-container text-error rounded-2xl flex items-center justify-center mx-auto shadow-sm">
              <AlertTriangle size={32} />
            </div>

            <div className="space-y-2">
              <h2 className="text-xl font-headline font-bold text-on-surface m-0">
                {this.props.fallbackTitle || 'Terjadi Kendala pada Halaman'}
              </h2>
              <p className="text-xs font-body text-on-surface-variant leading-relaxed opacity-80">
                Mohon maaf atas ketidaknyamanannya. Anda dapat mencoba memuat ulang halaman atau
                masuk kembali.
              </p>
            </div>

            {this.state.error && (
              <div className="p-3 bg-surface-container-low rounded-xl text-left overflow-auto max-h-32 text-[11px] font-mono text-error">
                {this.state.error.message}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="w-full sm:flex-1 py-3 px-4 bg-primary text-white rounded-xl font-bold text-xs flex items-center justify-center gap-2 hover:bg-primary-container transition-all active:scale-95 shadow-sm"
              >
                <RefreshCw size={15} />
                Muat Ulang
              </button>
              <button
                type="button"
                onClick={this.handleClearAndLogin}
                className="w-full sm:flex-1 py-3 px-4 bg-surface-container-high text-on-surface rounded-xl font-bold text-xs flex items-center justify-center gap-2 hover:bg-surface-container-highest transition-all active:scale-95"
              >
                <LogOut size={15} />
                Ke Halaman Login
              </button>
              <a
                href={BID_SUPPORT_WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Hubungi BID Support melalui WhatsApp"
                className="w-full sm:flex-1 py-3 px-4 bg-surface-container-high text-on-surface rounded-xl font-bold text-xs flex items-center justify-center gap-2 no-underline hover:bg-surface-container-highest transition-all active:scale-95"
              >
                <MessageCircle size={15} aria-hidden="true" />
                BID Support
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
